import React from 'react';
import { Clock, CheckCircle } from 'lucide-react';

// Helper function to evaluate mathematical expressions and fractions
export const evaluateAnswer = (ans) => {
  try {
    const normalized = ans.toString().trim().toLowerCase();
    // Check if it's a fraction (e.g., "99/2")
    if (normalized.includes('/') && !normalized.includes(' ')) {
      const parts = normalized.split('/');
      if (parts.length === 2) {
        const num = parseFloat(parts[0]);
        const den = parseFloat(parts[1]);
        if (!isNaN(num) && !isNaN(den) && den !== 0) {
          return num / den;
        }
      }
    }
    // Otherwise parse as a regular number
    return parseFloat(normalized);
  } catch {
    return ans; // Return as-is if evaluation fails
  }
};

// Shared function to check if answers match
export const checkAnswerCorrect = (userAnswer, correctAnswer) => {
  if (!userAnswer || correctAnswer === undefined || correctAnswer === null) {
    return false;
  }
  
  const userAnswerNormalized = userAnswer.toString().trim().toLowerCase();
  const correctAnswerNormalized = correctAnswer.toString().trim().toLowerCase();
  
  // Try exact string match first
  if (userAnswerNormalized === correctAnswerNormalized) {
    return true;
  }
  
  // Try numerical comparison (for math answers)
  const userValue = evaluateAnswer(userAnswerNormalized);
  const correctValue = evaluateAnswer(correctAnswerNormalized);
  
  // Only do numerical comparison if both are valid numbers
  if (!isNaN(userValue) && !isNaN(correctValue)) {
    // Use a much smaller tolerance - only allow rounding errors, not incomplete answers
    const tolerance = Math.abs(correctValue) * 1e-10; // Relative tolerance
    const minTolerance = 1e-10; // Absolute minimum tolerance
    return Math.abs(userValue - correctValue) <= Math.max(tolerance, minTolerance);
  }
  
  return false;
};

const TestPage = ({
  currentTest,
  currentQuestionIndex,
  setCurrentQuestionIndex,
  userAnswers,
  submitAnswer,
  nextQuestion,
  timeLeft,
  difficulty,
  testType,
  formatTime,
  setShowExitConfirm,
  correctAnswers = {},
  setCorrectAnswers
}) => {
  const currentItem = currentTest[currentQuestionIndex];
  const isCurrentCorrect = correctAnswers[currentItem.id];

  // Handle answer submission with auto-advance
  const handleAnswerChange = (e) => {
    const answer = e.target.value;
    
    // Don't allow changes if already marked correct
    if (isCurrentCorrect) {
      return;
    }
    
    // Update the answer in parent state
    submitAnswer(currentItem.id, answer);
    
    // Check if answer is correct (only if question has a correctAnswer or answer property)
    const correctAnswer = currentItem.correctAnswer ?? currentItem.answer;
    if (correctAnswer !== undefined && correctAnswer !== null) {
      const isCorrect = checkAnswerCorrect(answer, correctAnswer);
      
      if (isCorrect) {
        // Mark as correct
        setCorrectAnswers(prev => ({ ...prev, [currentItem.id]: true }));
        
        // Auto-advance
        if (currentQuestionIndex < currentTest.length - 1) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
          // Last question - pass the answer to nextQuestion
          nextQuestion(answer);
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-600 to-purple-800 flex items-center justify-center p-2 sm:p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-4 sm:p-8 relative mx-2">
        {/* Exit button */}
        <button
          onClick={() => setShowExitConfirm(true)}
          className="absolute top-2 right-2 sm:top-4 sm:right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center text-gray-600 hover:text-gray-800 transition-colors"
          title="Exit test"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 mr-10 sm:mr-12 gap-2 sm:gap-0">
          <div className="text-xs sm:text-sm text-gray-500">
            Question {currentQuestionIndex + 1} of {currentTest.length}
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <div className="flex items-center text-blue-600">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              <span className="text-xs sm:text-sm">{formatTime(timeLeft)}</span>
            </div>
            <div className="text-xs sm:text-sm text-gray-500 capitalize">
              {difficulty} - {testType.replace('_', ' ')}
            </div>
          </div>
        </div>

        <div className="mb-6 sm:mb-8">
          {isCurrentCorrect && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm text-green-700 font-medium">Correct! This answer is locked.</span>
            </div>
          )}
          
          <div className="text-lg sm:text-xl font-semibold mb-4 text-center">
            {currentItem.question}
          </div>
          
          {(testType === 'sequences' || testType === 'mental_math') ? (
            <div className="relative">
              <input
                key={currentItem.id}
                type="text"
                inputMode="number"
                step="any"
                value={userAnswers[currentItem.id] || ''}
                onChange={handleAnswerChange}
                placeholder="Enter your answer"
                className={`w-full p-3 sm:p-4 text-base sm:text-lg border-2 rounded-xl focus:outline-none text-center ${
                  isCurrentCorrect 
                    ? 'border-green-300 bg-green-50 cursor-not-allowed' 
                    : 'border-gray-200 focus:border-blue-500'
                }`}
                autoFocus={!isCurrentCorrect}
                disabled={isCurrentCorrect}
                readOnly={isCurrentCorrect}
              />
              {isCurrentCorrect && (
                <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-6 h-6 text-green-600" />
              )}
            </div>
          ) : (
            <div>
              <div className="relative">
                <textarea
                  key={currentItem.id}
                  value={userAnswers[currentItem.id] || ''}
                  onChange={handleAnswerChange}
                  placeholder="Enter your answer or approach"
                  className={`w-full p-3 sm:p-4 text-base sm:text-lg border-2 rounded-xl focus:outline-none h-32 resize-none ${
                    isCurrentCorrect 
                      ? 'border-green-300 bg-green-50 cursor-not-allowed' 
                      : 'border-gray-200 focus:border-blue-500'
                  }`}
                  autoFocus={!isCurrentCorrect}
                  disabled={isCurrentCorrect}
                  readOnly={isCurrentCorrect}
                />
                {isCurrentCorrect && (
                  <CheckCircle className="absolute right-3 top-3 w-6 h-6 text-green-600" />
                )}
              </div>
              <div className="mt-2 text-xs sm:text-sm text-gray-500">
                <strong>Hint:</strong> This is from {currentItem.firm_tag || 'various firms'}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2 sm:gap-4">
          <button
            onClick={() => {
              if (currentQuestionIndex > 0) {
                setCurrentQuestionIndex(currentQuestionIndex - 1);
              }
            }}
            disabled={currentQuestionIndex === 0}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors"
          >
            Previous
          </button>
          <button
            onClick={() => {
              // If it's the last question, pass the current answer
              if (currentQuestionIndex === currentTest.length - 1) {
                nextQuestion(userAnswers[currentItem.id]);
              } else {
                nextQuestion();
              }
            }}
            className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold text-sm sm:text-base hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
          >
            {currentQuestionIndex === currentTest.length - 1 ? 'Finish Test' : 'Next Question'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestPage;